<template>
  <div class="ai-chat-container">
    <!-- 全屏加载覆盖 -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="loader-card">
        <div class="spinner"></div>
        <div class="loader-text">正在加载...</div>
      </div>
    </div>
    <div class="chat-content-wrapper">
      <!-- 聊天消息区域 -->
      <div class="chat-messages" ref="messagesContainer">
        <!-- 欢迎消息 -->
        <div v-if="messages.length === 0" class="welcome-message">
          <div class="welcome-icon">🤖</div>
          <h2 class="welcome-title">AI 小森助理</h2>
          <p class="welcome-text">你好！我是你的智能助手，有什么可以帮你的吗？</p>
          <div class="welcome-suggestions">
            <button 
              v-for="(suggestion, index) in suggestions" 
              :key="index"
              class="suggestion-btn"
              @click="sendSuggestion(suggestion)">
              {{ suggestion }}
            </button>
          </div>
        </div>

        <!-- 聊天消息列表 -->
        <div v-for="(msg, index) in messages" :key="index" 
             :class="['message-wrapper', msg.role === 'user' ? 'user-message' : 'ai-message']">
          <div class="message-avatar">
            <span v-if="msg.role === 'user'">👤</span>
            <span v-else>🤖</span>
          </div>
          <div class="message-content">
            <div class="message-bubble">
              <div class="message-text" v-html="formatMessage(msg.content)"></div>
            </div>
            <div class="message-time">{{ formatTime(msg.createTime) }}</div>
          </div>
        </div>

        <!-- 加载中 -->
        <div v-if="isLoading" class="message-wrapper ai-message">
          <div class="message-avatar">
            <span>🤖</span>
          </div>
          <div class="message-content">
            <div class="message-bubble loading-bubble">
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 输入区域 -->
      <div class="chat-input-area">
        <div class="input-container">
          <div class="input-toolbar">
            <button class="toolbar-btn" @click="clearHistory" title="清空历史">
              <span>🗑️</span>
            </button>
          </div>
          <div class="input-wrapper">
            <textarea 
              v-model="inputMessage"
              class="chat-input"
              placeholder="输入消息..."
              rows="1"
              @keydown.enter.exact.prevent="sendMessage"
              @input="autoResize"
              ref="inputTextarea"
            ></textarea>
            <button 
              class="send-btn" 
              @click="sendMessage"
              :disabled="!inputMessage.trim() || isLoading">
              <span v-if="!isLoading">📤</span>
              <span v-else class="loading-spinner">⏳</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import * as aiApi from '../api/ai'

export default {
  name: 'AiChat',
  setup() {
    const messages = ref([])
    const inputMessage = ref('')
    const isLoading = ref(false)
    const messagesContainer = ref(null)
    const inputTextarea = ref(null)
    const pageLoading = ref(true)

    const suggestions = [
      '帮我分析一下今天的任务',
      '如何提高工作效率？',
      '给我一些时间管理建议',
      '如何平衡工作和生活？'
    ]

    // 加载聊天历史
    const loadHistory = async () => {
      pageLoading.value = true
      try {
        const res = await aiApi.getChatHistory(50)
        if (res.data.code === 200) {
          messages.value = res.data.data || []
          await nextTick()
          scrollToBottom()
        }
      } catch (error) {
        console.error('加载历史失败', error)
      } finally {
        pageLoading.value = false
      }
    }

    // 发送消息
    const sendMessage = async () => {
      if (!inputMessage.value.trim() || isLoading.value) return

      const userMessage = inputMessage.value.trim()
      inputMessage.value = ''
      
      // 添加用户消息到界面
      messages.value.push({
        role: 'user',
        content: userMessage,
        createTime: new Date().toISOString()
      })

      await nextTick()
      scrollToBottom()

      // 发送到后端
      isLoading.value = true
      try {
        const res = await aiApi.sendMessage(userMessage)
        if (res.data.code === 200) {
          // 添加AI回复
          messages.value.push({
            role: 'assistant',
            content: res.data.data,
            createTime: new Date().toISOString()
          })
          await nextTick()
          scrollToBottom()
        } else {
          ElMessage.error(res.data.message || '发送失败')
        }
      } catch (error) {
        ElMessage.error('发送失败，请稍后重试')
        console.error('发送消息失败', error)
      } finally {
        isLoading.value = false
      }
    }

    // 发送建议消息
    const sendSuggestion = (suggestion) => {
      inputMessage.value = suggestion
      sendMessage()
    }

    // 清空历史
    const clearHistory = async () => {
      try {
        await ElMessageBox.confirm('确定要清空所有聊天记录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })

        const res = await aiApi.clearChatHistory()
        if (res.data.code === 200) {
          messages.value = []
          ElMessage.success('已清空聊天记录')
        } else {
          ElMessage.error(res.data.message || '清空失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('清空失败')
          console.error('清空历史失败', error)
        }
      }
    }

    // 格式化消息（支持换行）
    const formatMessage = (content) => {
      return content.replace(/\n/g, '<br>')
    }

    // 格式化时间
    const formatTime = (time) => {
      if (!time) return ''
      const date = new Date(time)
      const now = new Date()
      const diff = now - date

      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
      
      return `${date.getMonth() + 1}月${date.getDate()}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    // 滚动到底部
    const scrollToBottom = () => {
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }

    // 自动调整输入框高度
    const autoResize = () => {
      const textarea = inputTextarea.value
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
      }
    }

    onMounted(() => {
      loadHistory()
    })

    return {
      messages,
      inputMessage,
      isLoading,
      messagesContainer,
      inputTextarea,
      suggestions,
      sendMessage,
      sendSuggestion,
      clearHistory,
      formatMessage,
      formatTime,
      autoResize,
      pageLoading
    }
  }
}
</script>

<style scoped>
/* 全屏加载覆盖样式（苹果风格） */
.page-loading-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(242, 242, 247, 0.8);
  backdrop-filter: saturate(180%) blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}
.loader-card {
  background: white;
  border: 1px solid #e5e5ea;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.08);
  display: flex;
  align-items: center;
  gap: 12px;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #e5e5ea;
  border-top-color: #007AFF;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
.loader-text { font-weight: 700; color: #1d1d1f; }
@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }

.ai-chat-container {
  height: 100%;
  min-height: calc(100vh - 60px);
  background: #F2F2F7;
  overflow: hidden;
  position: relative;
}

.chat-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-bottom: 200px;
  -webkit-overflow-scrolling: touch;
}

/* 欢迎消息 */
.welcome-message {
  text-align: center;
  padding: 60px 20px;
}

.welcome-icon {
  font-size: 64px;
  margin-bottom: 20px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.welcome-title {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1f;
  margin-bottom: 12px;
}

.welcome-text {
  font-size: 16px;
  color: #86868b;
  margin-bottom: 32px;
}

.welcome-suggestions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
}

.suggestion-btn {
  padding: 16px 20px;
  background: white;
  border: 1px solid #D1D1D6;
  border-radius: 16px;
  font-size: 14px;
  color: #1d1d1f;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.suggestion-btn:hover {
  background: #F9F9F9;
  border-color: #007AFF;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.15);
}

/* 消息样式 */
.message-wrapper {
  display: flex;
  margin-bottom: 20px;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-message {
  flex-direction: row-reverse;
}

.message-avatar {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  margin: 0 12px;
}

.user-message .message-avatar {
  background: linear-gradient(135deg, #34C759 0%, #30D158 100%);
}

.message-content {
  max-width: 70%;
  display: flex;
  flex-direction: column;
}

.user-message .message-content {
  align-items: flex-end;
}

.message-bubble {
  background: white;
  padding: 12px 16px;
  border-radius: 18px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.user-message .message-bubble {
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  color: white;
}

.message-text {
  font-size: 15px;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-time {
  font-size: 11px;
  color: #86868b;
  margin-top: 4px;
  padding: 0 8px;
}

/* 加载动画 */
.loading-bubble {
  padding: 16px 20px;
}

.typing-indicator {
  display: flex;
  gap: 6px;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #86868b;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

/* 输入区域 */
.chat-input-area {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 800px;
  z-index: 10;
}

.input-container {
  background: white;
  border-radius: 20px;
  padding: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
}

.input-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.toolbar-btn {
  padding: 6px 10px;
  background: #F2F2F7;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  color: #86868b;
}

.toolbar-btn:hover {
  background: #E5E5EA;
  color: #1d1d1f;
}

.input-wrapper {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.chat-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #D1D1D6;
  border-radius: 20px;
  font-size: 15px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  resize: none;
  outline: none;
  transition: all 0.2s;
  max-height: 120px;
}

.chat-input:focus {
  border-color: #007AFF;
  box-shadow: 0 0 0 3px rgba(0, 122, 255, 0.1);
}

.send-btn {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #007AFF 0%, #5856D6 100%);
  border: none;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.send-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 移动端优化 */
@media (max-width: 768px) {
  .ai-chat-container {
    min-height: calc(100vh - 56px - 60px);
  }

  .chat-messages {
    padding: 12px;
    padding-bottom: 180px;
  }

  .chat-input-area {
    bottom: 20px;
    width: 95%;
  }

  .input-container {
    padding: 12px;
  }

  .welcome-message {
    padding: 40px 16px;
  }

  .welcome-icon {
    font-size: 48px;
  }

  .welcome-title {
    font-size: 24px;
  }

  .message-content {
    max-width: 80%;
  }

  .message-avatar {
    width: 32px;
    height: 32px;
    font-size: 16px;
    margin: 0 8px;
  }

  .message-bubble {
    padding: 10px 14px;
  }

  .message-text {
    font-size: 14px;
  }



  .chat-input {
    padding: 10px 14px;
    font-size: 14px;
  }

  .send-btn {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
}
</style>
